// import

import {styled} from 'goober';
import type {ComponentProps} from 'react';

import type {HomeMainNode} from 'libs/model';
import {LightBulb} from 'modules/Icon';
import {Type, paraToBreaks} from 'modules/Type';

import {NotifyForm} from './NotifyForm';

// types

type Props = ComponentProps<typeof Block> &
HomeMainNode['meta']['notifyRegion'];

// component

export function HomeNotify(props: Props) {
  const {headline, buttonText, mailerliteGroups, ...rest} = props;

  return (
    <Block {...rest} data-test="notify">
      <div className="sep head">
        <div className="beam b1" />
        <div className="beam b2" />
        <div className="beam b3" />
        <LightBulb className="icon" />
      </div>

      <div className="region">
        <div className="titleInner">
          <Type as="header" className="header">
            <h1
              className="title"
              dangerouslySetInnerHTML={{__html: paraToBreaks(headline)}}
            />
          </Type>
        </div>

        <NotifyForm
          buttonText={buttonText}
          className="formInner"
          groups={mailerliteGroups}
        />
      </div>
    </Block>
  );
}

// styles

const Block = styled('section')`
  position: relative;
  z-index: 1;
  filter:
    drop-shadow(0 0.25rem 0 var(--orange))
    drop-shadow(0 -0.25rem 0 var(--orange))
    drop-shadow(0 0.25rem 0 var(--red))
    drop-shadow(0 -0.25rem 0 var(--red));

  .icon {
    width: 12vw;
    position: absolute;
    top: 1.5rem;
    left: calc(50% - 6vw);
    color: var(--black);
    filter:
      drop-shadow(0 0.125rem 0 var(--orange))
      drop-shadow(0 0.125rem 0 var(--red));
  }

  .region {
    padding: 1.5rem 1rem 6rem;
    position: relative;
    background-color: var(--yellow);
  }

  .titleInner {
    margin: 0 auto;
    max-width: var(--bp-ml);
  }

  .formInner {
    margin: 3rem auto 0;
    max-width: var(--bp-sm);
  }

  .title {
    font-weight: 400;
    font-size: 2.5rem;
    line-height: 3rem;
    text-align: center;

    & strong {
      font-weight: 800;
      font-variation-settings: "wght" 800;
      text-transform: uppercase;
    }

    &::before {
      content: none;
    }
  }

  & .beam {
    background-color: var(--yellow);
    border-radius: 100rem 100rem 0 0;
    margin: 0 auto;
  }

  & .b1 {
    width: 25%;
    padding-top: 12.5%;
  }

  & .b2 {
    width: 75%;
    padding-top: 3rem;
  }

  & .b3 {
    width: 87.5%;
    padding-top: 3rem;
  }

  @media (min-width: 36em) and (max-width: 53.99875em) {
    .region {
      padding-top: 4.5rem;
    }

    .formInner {
      max-width: 36rem;
    }

    .title {
      font-size: 2.25rem;
      line-height: 2.75rem;
    }

    .b2, .b3 {
      padding-top: 1.5rem;
    }
  }

  @media (max-width: 35.99875em) {
    .region {
      padding-top: 4.5rem;
      padding-bottom: 4.5rem;
    }

    .title {
      font-size: 2rem;
      line-height: 2.5rem;
    }

    .b2, .b3 {
      padding-top: 1.5rem;
    }

    .b3 {
      display: none;
    }
  }
`;
